import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from "react-router-dom";
// REPLACE WITH YOUR ACTUAL IMPORTS:
// import useResendConfirmation from "../../hooks/Auth/useResendConfirmation";

// Mock resend confirmation hook - REPLACE with real implementation
const useResendConfirmation = () => ({
  resendConfirmation: useCallback(async (email: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    return { success: true };
  }, []),
  isLoading: false,
  error: null
});

// Icons
import { 
  Mail, CheckCircle, User, Building2, Clock, RefreshCw, 
  ArrowRight, Shield, AlertCircle, ExternalLink, Loader2,
  Check, ChevronRight, Home
} from 'lucide-react';

interface LocationState {
  registrationComplete?: boolean;
  userId?: string;
  practiceId?: string;
  email?: string;
  userSub?: string;
  role?: string;
}

const ConfirmEmailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resendConfirmation, error: resendError } = useResendConfirmation();
  
  // Extract data from navigation state
  const locationState = location.state as LocationState || {};
  const {
    registrationComplete = false,
    userId,
    practiceId,
    email,
    userSub,
    role
  } = locationState;

  // Component state
  const [resendCount, setResendCount] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle resend confirmation email
  const handleResendEmail = useCallback(async () => {
    if (!email || countdown > 0 || isResending) return;
    
    setIsResending(true);
    setResendSuccess(false);
    
    try {
      const result = await resendConfirmation(email);
      
      if (result.success) {
        setResendCount(prev => prev + 1);
        setResendSuccess(true);
        setCountdown(60); // 60 second cooldown
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setResendSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Failed to resend confirmation:', error);
    } finally {
      setIsResending(false);
    }
  }, [email, countdown, isResending, resendConfirmation]);

  // Navigate to login
  const goToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);


  // Email domain for help text
  const emailDomain = useMemo(() => {
    if (!email) return '';
    return email.split('@')[1];
  }, [email]);

  // Format registration time
  const registrationTime = useMemo(() => {
    return currentTime.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [currentTime]);

  // If no email provided, show error state
  if (!email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white p-8 shadow-2xl rounded-2xl border border-gray-100 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Registration Error
            </h2>
            <p className="text-gray-600 mb-6">
              No email address found. Please complete the registration process first.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
            >
              Go to Registration
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-4xl"
      >
        <div className="bg-white p-8 md:p-10 shadow-2xl rounded-2xl border border-gray-100">
          {/* Header Section */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Mail className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Check Your Email
            </h1>
            <p className="text-lg text-gray-600">
              We've sent a confirmation link to verify your account
            </p>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Left Column - Email Instructions */}
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-6 h-6 mr-2 text-emerald-600" />
                  Registration Complete!
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Mail className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Confirmation Email Sent</p>
                      <p className="text-sm text-gray-600 mt-1">
                        We've sent a verification link to <span className="font-medium text-emerald-700">{email}</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Clock className="w-5 h-5 text-emerald-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Registered at</p>
                      <p className="text-sm text-gray-600 mt-1">{registrationTime}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Next Steps
                </h3>
                <div className="space-y-3">
                  {[
                    "Check your email inbox and spam folder",
                    "Click the verification link in the email",
                    "Your account will be activated automatically",
                    "Sign in to access your practice dashboard"
                  ].map((step, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-gray-700">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email Help */}
              <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-amber-600" />
                  Don't see the email?
                </h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p>• Check your spam or junk folder</p>
                  <p>• Make sure {email} is correct</p>
                  {emailDomain && (
                    <p>• Check if {emailDomain} has email filtering enabled</p>
                  )}
                  <p>• The email may take a few minutes to arrive</p>
                </div>
              </div>
            </div>

            {/* Right Column - Account Details & Actions */}
            <div className="space-y-6">
              {/* Account Summary */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Account Summary
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Shield className="w-5 h-5 text-emerald-600 mr-2" />
                      <span className="text-gray-700">Status</span>
                    </div>
                    <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      Pending Verification
                    </span>
                  </div>
                </div>
              </div>

              {/* Resend Email */}
              <div className="bg-white rounded-xl p-6 border-2 border-dashed border-gray-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Didn't receive the email?
                </h3>
                
                <AnimatePresence>
                  {resendSuccess && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4"
                    >
                      <div className="flex items-center">
                        <Check className="w-5 h-5 mr-2" />
                        Confirmation email sent successfully!
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button
                  onClick={handleResendEmail}
                  disabled={countdown > 0 || isResending}
                  whileHover={{ scale: countdown > 0 || isResending ? 1 : 1.02 }}
                  whileTap={{ scale: countdown > 0 || isResending ? 1 : 0.98 }}
                  className={`w-full flex items-center justify-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    countdown > 0 || isResending
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg"
                  }`}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : countdown > 0 ? (
                    <>
                      <Clock className="w-5 h-5 mr-2" />
                      Resend in {countdown}s
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2" />
                      Resend Confirmation Email
                      {resendCount > 0 && (
                        <span className="ml-2 text-sm opacity-75">
                          ({resendCount} sent)
                        </span>
                      )}
                    </>
                  )}
                </motion.button>

                <p className="text-sm text-gray-500 mt-3 text-center">
                  Make sure to check your spam folder after requesting a new email
                </p>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <motion.button
                    onClick={goToLogin}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
                  >
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-indigo-600 mr-3" />
                      <span className="font-medium text-gray-900">Go to Sign In</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </motion.button>
                  
                  <motion.button
                    onClick={() => window.open(`https://${emailDomain}`, '_blank')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all duration-200"
                  >
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-blue-600 mr-3" />
                      <span className="font-medium text-gray-900">Open {emailDomain}</span>
                    </div>
                    <ExternalLink className="w-5 h-5 text-gray-400" />
                  </motion.button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-500">
                Having trouble? Contact our support team for assistance.
              </div>
              
              <div className="flex gap-3">
                <motion.button
                  onClick={() => navigate('/')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </motion.button>
                
                <motion.button
                  onClick={goToLogin}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all duration-200"
                >
                  Continue to Sign In
                  <ArrowRight className="w-4 h-4 ml-2" />
                </motion.button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          <AnimatePresence>
            {resendError && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl"
              >
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  <span>Failed to resend email. Please try again later.</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default ConfirmEmailPage;
